import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {ChecklistResourceType} from '@dms/shared';

import {checklistFormSchema, ChecklistFormValue} from '../types/ChecklistFormValue';
import {checklistTaskFactory} from '../utils/checklistTaskFactory';
import {TasksControl} from './TasksControl';

export interface ChecklistFormProps {
  onSubmit: FormSubmitHandler<ChecklistFormValue>;
  isEditing?: boolean;
  isLoading?: boolean;
  defaultValues?: ChecklistFormValue;
}

export function ChecklistForm(props: ChecklistFormProps) {
  const getDefaultValues = (): ChecklistFormValue => {
    if (props.defaultValues) {
      return props.defaultValues;
    }

    return {
      name: '',
      resourceId: '',
      description: '',
      tasks: [checklistTaskFactory()],
    };
  };

  return (
    <Form<ChecklistFormValue>
      defaultValues={getDefaultValues()}
      experimentalZodSchema={checklistFormSchema}
      onSubmit={props.onSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <Box paddingBottom={20}>
          <VStack spacing={6} maxWidth={200}>
            <HStack spacing={4} width="100%">
              <Box width="100%">
                <FormField
                  type="text"
                  control={control}
                  name="name"
                  label={i18n.t('general.labels.name')}
                  data-testid={testIds.settings.generalChecklistCreate('checklistName')}
                  isRequired
                />
              </Box>
              <Box width="100%">
                <FormField
                  type="choice"
                  control={control}
                  name="resourceId"
                  label={i18n.t('page.settings.generalChecklist.resources')}
                  data-testid={testIds.settings.generalChecklistCreate('selectResource')}
                  options={[
                    {
                      value: ChecklistResourceType.BUSINESS_CASE,
                      label: i18n.t('page.businessCase.labels.businessCase'),
                    },
                    {
                      value: ChecklistResourceType.CUSTOMER,
                      label: i18n.t('entity.customer.labels.customer'),
                    },
                    {
                      value: ChecklistResourceType.INTEREST,
                      label: i18n.t('entity.interest.labels.interest'),
                    },
                    {
                      value: ChecklistResourceType.SERVICE_CASE,
                      label: i18n.t('page.workshop.labels.serviceCase'),
                    },
                    {
                      value: ChecklistResourceType.SALE_VEHICLE,
                      label: i18n.t('page.settings.generalChecklist.saleVehicle'),
                    },
                    {
                      value: ChecklistResourceType.SERVICE_VEHICLE,
                      label: i18n.t('page.settings.generalChecklist.serviceVehicle'),
                    },
                    {
                      value: ChecklistResourceType.WAREHOUSE_ARTICLE,
                      label: i18n.t('page.settings.generalChecklist.warehouseArticle'),
                    },
                    {
                      value: ChecklistResourceType.DIRECT_SALE,
                      label: i18n.t('page.settings.generalChecklist.directSale'),
                    },
                    {
                      value: ChecklistResourceType.RECEIVE_NOTE,
                      label: i18n.t('page.settings.generalChecklist.receiveNote'),
                    },
                    {
                      value: ChecklistResourceType.RECEIVE_NOTE_CORRECTION,
                      label: i18n.t('page.settings.generalChecklist.receiveNoteCorrection'),
                    },
                    {
                      value: ChecklistResourceType.SERVICE_ORDER_ISSUE,
                      label: i18n.t('page.settings.generalChecklist.serviceOrderIssue'),
                    },
                    {
                      value: ChecklistResourceType.SERVICE_ORDER_RETURNS,
                      label: i18n.t('page.settings.generalChecklist.serviceOrderReturns'),
                    },
                    {
                      value: ChecklistResourceType.SERVICE_ORDER,
                      label: i18n.t('page.settings.generalChecklist.serviceOrder'),
                    },
                  ]}
                  isRequired
                />
              </Box>
            </HStack>
            <FormField
              type="textarea"
              control={control}
              name="description"
              data-testid={testIds.settings.generalChecklistCreate('description')}
              minRows={4}
              label={i18n.t('general.labels.description')}
              isCounterVisible
              maxLength={255}
              isResizable
            />
            <TasksControl formApi={formApi} control={control} />
            <SettingsFooter
              data-testid={testIds.settings.generalChecklistCreate('actions')}
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discardChanges'),
                  variant: 'secondary',
                  onClick: () =>
                    props.isEditing
                      ? navigateWithoutBlocker(settingsRoutes.generalChecklist)
                      : navigateWithoutBlocker(-1),
                  isDisabled: props.isLoading,
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: i18n.t('general.actions.saveAndClose'),
                  isLoading: props.isLoading,
                },
              ]}
            />
          </VStack>
        </Box>
      )}
    </Form>
  );
}
